import { useState, useRef, useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils.js";
import { TIME_RANGE_OPTIONS } from "@/constants/chart-constants.js";
import TagListPanel from "./tag-list-panel.jsx";
import TrendAnalysisPanel from "./trend-analysis-panel.jsx";
import { useAssetTagGroups } from "@/hooks/useAssetTagGroups.js";

/** Matches drag handle `minWidth` on the splitter */
const SPLIT_DIVIDER_PX = 4;
/** Room for time-range pills (24 Hours, 7 Days, …) beside the tag list */
const MIN_TREND_PANEL_PX = 320;

/**
 * @param {number} rawPercent - Desired left width as % of container
 * @param {number} containerWidthPx
 */
function clampLeftPanelPercent(rawPercent, containerWidthPx) {
  if (containerWidthPx <= 0) return rawPercent;
  const maxLeftByTrendMin =
    ((containerWidthPx - SPLIT_DIVIDER_PX - MIN_TREND_PANEL_PX)
      / containerWidthPx)
    * 100;
  let p = rawPercent;
  p = Math.min(p, 70);
  p = Math.max(p, 15);
  p = Math.min(p, maxLeftByTrendMin);
  return p;
}

/**
 * Shared component for displaying tag list panel and trend analysis panel
 * Used by both Asset Vitals and Asset Trends pages
 * @param {Object} props - Component props
 * @param {Object} props.tagTrendData - Data from useAssetTagTrend hook (contains tagData, trendColumns, etc.)
 * @param {Object} props.compressor - The compressor/asset object
 * @param {Array} props.downtimeEvents - Array of downtime events
 * @param {Object} props.layout - Layout configuration
 * @param {string} props.layout.type - 'vitals' or 'trends'
 * @param {boolean} props.layout.showTrendAlways - Whether to always show trend panel
 * @returns {JSX.Element} Asset tag trend component
 */
export default function AssetTagTrend({
  tagTrendData,
  compressor,
  downtimeEvents,
  layout = { type: "vitals", showTrendAlways: false },
}) {
  const { groups, isLoadingGroups, selectedGroupId, setSelectedGroupId } =
    useAssetTagGroups(compressor?.id || null);

  const {
    allParameters,
    isLoading,
    isLoadingEnalysisTrendValues,
    error,
    trendColumns,
    trendColumnRawData,
    isLoadingTrendColumns,
    searchTerm,
    setSearchTerm,
    selectedStatus,
    setSelectedStatus,
    selectedCategory,
    setSelectedCategory,
    selectedParameters,
    setSelectedParameters,
    sortColumn,
    sortDirection,
    handleSort,
    selectedTimeRange,
    setSelectedTimeRange,
    previousTimeRange,
    setPreviousTimeRange,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    applyCustomDateRange,
    trendData,
    isLoadingTrend,
    trendError,
    isZoomed,
    setIsZoomed,
    getParameterId,
    formatValue,
    handleRefreshTrendData,
    createChartSeriesMemo,
    formatMultiSeriesTooltipMemo,
  } = tagTrendData;

  // Resizable divider state
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    // Default widths: 30% for trends, 20% for vitals
    return layout.type === "trends" ? 30 : 20;
  });
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef(null);
  const dividerRef = useRef(null);

  /** Percent widths only apply at lg+; below that, list and trend stack at full width */
  const [isLgSplitLayout, setIsLgSplitLayout] = useState(
    () =>
      typeof window !== "undefined"
      && window.matchMedia("(min-width: 1024px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const sync = () => setIsLgSplitLayout(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Determine if trend panel should be shown
  const shouldShowTrendPanel =
    layout.showTrendAlways || selectedParameters.size > 0;

  // Handle mouse down on divider
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;

      const percentage = clampLeftPanelPercent(
        (mouseX / containerWidth) * 100,
        containerWidth
      );

      setLeftPanelWidth(percentage);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  // If the container shrinks, pull the divider back so the trend toolbar stays usable (split layout only)
  useEffect(() => {
    if (!shouldShowTrendPanel || !isLgSplitLayout || !containerRef.current) {
      return;
    }
    const el = containerRef.current;
    const clampToContainer = () => {
      const w = el.getBoundingClientRect().width;
      setLeftPanelWidth((prev) => clampLeftPanelPercent(prev, w));
    };
    const ro = new ResizeObserver(clampToContainer);
    ro.observe(el);
    clampToContainer();
    return () => ro.disconnect();
  }, [shouldShowTrendPanel, isLgSplitLayout]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
        <span className='ml-3 text-muted-foreground'>
          Loading operational data...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center py-8'>
        <div className='text-center'>
          <AlertTriangle className='h-8 w-8 text-red-500 mx-auto mb-2' />
          <p className='text-red-600 mb-2'>{error}</p>
        </div>
      </div>
    );
  }

  // Wrap panels with height constraints
  // On small screens (stacked): tag panel gets min-height, trend panel gets fixed height
  // On large screens (side-by-side): both panels fill available height
  const renderPanel = (panel, isTrend = false) => {
    if (isTrend) {
      return (
        <div className='h-full min-h-[500px] w-full max-w-full lg:min-h-0'>
          {panel}
        </div>
      );
    }
    return (
      <div className='h-full min-h-[400px] w-full max-w-full min-w-0 lg:min-h-0'>
        {panel}
      </div>
    );
  };

  // For mobile/tablet, use stacked layout
  if (!shouldShowTrendPanel) {
    return (
      <div className='h-auto w-full max-w-full'>
        {renderPanel(
          <TagListPanel
            allParameters={allParameters}
            groups={groups}
            isLoadingGroups={isLoadingGroups}
            selectedParameters={selectedParameters}
            setSelectedParameters={setSelectedParameters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedGroupId={selectedGroupId}
            setSelectedGroupId={setSelectedGroupId}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            getParameterId={getParameterId}
            formatValue={formatValue}
            trendColumns={trendColumns}
            trendColumnRawData={trendColumnRawData}
            isLoadingTrendColumns={isLoadingTrendColumns}
            isLoadingEnalysisTrendValues={isLoadingEnalysisTrendValues}
            compactFilterToolbar={false}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className='flex h-auto w-full max-w-full flex-col gap-6 lg:h-[calc(100vh-220px)] lg:gap-0 lg:flex-row'>
      {/* Left Column: Tag List Panel */}
      <div
        className='w-full min-w-0 shrink-0 overflow-hidden lg:w-auto lg:shrink-0'
        style={{
          minHeight: "400px",
          ...(shouldShowTrendPanel
            && isLgSplitLayout && {
              width: `calc(${leftPanelWidth}% - 2px)`,
            }),
        }}>
        {renderPanel(
          <TagListPanel
            allParameters={allParameters}
            groups={groups}
            isLoadingGroups={isLoadingGroups}
            selectedParameters={selectedParameters}
            setSelectedParameters={setSelectedParameters}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedGroupId={selectedGroupId}
            setSelectedGroupId={setSelectedGroupId}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            getParameterId={getParameterId}
            formatValue={formatValue}
            trendColumns={trendColumns}
            trendColumnRawData={trendColumnRawData}
            isLoadingTrendColumns={isLoadingTrendColumns}
            isLoadingEnalysisTrendValues={isLoadingEnalysisTrendValues}
            compactFilterToolbar={shouldShowTrendPanel}
          />
        )}
      </div>

      {/* Resizable Divider - Only show on large screens when both panels are visible */}
      {shouldShowTrendPanel && (
        <div
          ref={dividerRef}
          onMouseDown={handleMouseDown}
          className={cn(
            "hidden lg:flex w-1 bg-border hover:bg-primary cursor-col-resize transition-colors flex-shrink-0",
            isResizing && "bg-primary"
          )}
          style={{
            minWidth: "4px",
          }}>
          <div className='w-full h-full' />
        </div>
      )}

      {/* Right Column: Trend Analysis Container */}
      {shouldShowTrendPanel && (
        <div
          className='min-h-[500px] w-full min-w-0 flex-1 overflow-hidden lg:min-h-0 lg:w-auto'
          style={
            shouldShowTrendPanel && isLgSplitLayout
              ? { width: `calc(${100 - leftPanelWidth}% - 2px)` }
              : undefined
          }>
          {renderPanel(
            <TrendAnalysisPanel
              selectedParameters={selectedParameters}
              selectedTimeRange={selectedTimeRange}
              setSelectedTimeRange={setSelectedTimeRange}
              previousTimeRange={previousTimeRange}
              setPreviousTimeRange={setPreviousTimeRange}
              customStartDate={customStartDate}
              setCustomStartDate={setCustomStartDate}
              customEndDate={customEndDate}
              setCustomEndDate={setCustomEndDate}
              applyCustomDateRange={applyCustomDateRange}
              timeRangeOptions={TIME_RANGE_OPTIONS}
              trendData={trendData}
              isLoadingTrend={isLoadingTrend}
              trendError={trendError}
              isZoomed={isZoomed}
              setIsZoomed={setIsZoomed}
              fetchTrendData={handleRefreshTrendData}
              createChartSeries={createChartSeriesMemo}
              formatMultiSeriesTooltip={formatMultiSeriesTooltipMemo}
              downtimeEvents={downtimeEvents}
              assetId={compressor?.id}
            />,
            true
          )}
        </div>
      )}
    </div>
  );
}
